import { ref } from 'vue';
import type { ResponseError } from '../plugins/api/api';
import type { Dictionary } from '@/core/types/gengeric';
import { getErrorSnackText, isCustomErrorType, isWarningErrorType } from '@/parts/errors/utils/error-types';

type ErrorMessagePayload = {
  timeout?: number;
  requestError?: {
    errors: ResponseError[];
    traceId?: string;
    url?: string;
    response: {
      errors: ResponseError[];
    };
    operation: {
      headers: Dictionary<string>;
      query: string;
      variables: Dictionary<unknown>;
    };
    status: number;
  };
  innerError?: {
    name: string;
    message: string;
    stack: string | undefined;
  };
};

export enum ErrorMessageType {
  Error = 'error',
  Warning = 'warning',
}

type ErrorMessageConfig = {
  type: ErrorMessageType;
  title?: string;
  message?: string;
  textareaMessage?: string;
  timeout?: number;
};

const errorMessageConfig = ref<ErrorMessageConfig>();
const isShown = ref(false);

export const useErrorMessage = () => {
  const showErrorMessage = (payload: ErrorMessagePayload) => {
    if (payload.requestError) {
      if (!Array.isArray(payload.requestError.errors)) {
        return;
      }

      const firstError = payload.requestError.errors[0];

      if (isCustomErrorType(firstError.__typename)) {
        return;
      }

      const isWarningType = isWarningErrorType(firstError.__typename);

      let title: string | undefined = '';
      let message = '';

      if (isWarningType) {
        const errorPayload = getErrorSnackText(firstError.__typename, firstError);

        title = errorPayload.title || 'Запрос не может быть выполнен';
        message = errorPayload.message;
      }

      if (payload.requestError.status === 403) {
        title = 'Доступ запрещен';
      }

      let response = '';

      if (!isWarningType && Array.isArray(payload.requestError.errors)) {
        response = payload.requestError.errors.map((error) => error.message).join('\n');
      }

      errorMessageConfig.value = {
        type: isWarningType ? ErrorMessageType.Warning : ErrorMessageType.Error,
        timeout: payload.timeout,
        title,
        message,
        textareaMessage: `trace-id: ${payload.requestError.traceId}\n\nurl: ${payload.requestError.url}\n\nstatus code: ${payload.requestError.status}\n\nresponse: ${response} \n${JSON.stringify(payload.requestError.response)}\n\nconfig: ${JSON.stringify(payload.requestError.operation)}`,
      };
    } else if (payload.innerError) {
      errorMessageConfig.value = {
        type: ErrorMessageType.Error,
        timeout: payload.timeout,
        textareaMessage: `${payload.innerError.name ? `name: ${payload.innerError.name}\n\n` : ''}${payload.innerError.message ? `message: ${payload.innerError.message}\n\n` : ''}${payload.innerError.stack ? `stack: ${payload.innerError.stack}` : ''}`,
      };
    }

    isShown.value = true;
  };

  const close = () => (isShown.value = false);

  return {
    isShown,
    errorMessageConfig,
    showErrorMessage,
    close,
  };
};
